import { useEffect, useState } from 'react';

import {
  IonButton,
  IonCard,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonLabel,
  IonRadio,
  IonRadioGroup,
  IonToast,
  IonToggle
} from '@ionic/react';
import { checkmark } from 'ionicons/icons';

import styles from './PricingCard.module.scss';

import { DEFAULT_BILLED_ANNUALLY } from '../../constants/pricing';
import {
  PaddleProducts,
  Plan,
  StandardPaddleProduct,
  StandardPaddleProductWithAdditional
} from '../../types/pricing.types';
import { GitLiveLogo } from '../GitLiveLogo/GitLiveLogo';

export interface PricingCardProps {
  enterprise: boolean;
  generatePaymentLink: (plan: Plan, billedAnnually: boolean) => string;
  products: PaddleProducts;
}

export const PricingCard = ({
  enterprise,
  products,
  generatePaymentLink
}: PricingCardProps) => {
  const [personalBilledAnnually, setPersonalBilledAnnually] = useState(
    DEFAULT_BILLED_ANNUALLY
  );
  const [teamBilledAnnually, setTeamBilledAnnually] = useState(
    DEFAULT_BILLED_ANNUALLY
  );
  const [selectedPlan, setSelectedPlan] = useState<Plan>(
    enterprise ? Plan.Enterprise : Plan.Team
  );
  const [buttonText, setButtonText] = useState('Select a pricing plan');
  const [paymentLink, setPaymentLink] = useState<string>();
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    if (selectedPlan) {
      const { buttonText, paymentLink } = getPlanDetails(selectedPlan);
      setButtonText(buttonText);
      setPaymentLink(paymentLink);
    }
  }, [selectedPlan, personalBilledAnnually, teamBilledAnnually]);

  const copyPaymentLink = () => {
    if (!selectedPlan) return;

    navigator.clipboard.writeText(
      generatePaymentLink(selectedPlan, teamBilledAnnually)
    );
    setLinkCopied(true);
  };

  const getPlanDetails = (plan: Plan) => {
    if (plan === Plan.Free) {
      return {
        buttonText: 'Invite more team members',
        paymentLink: '/invite-teammates'
      };
    }
    const billedAnnually =
      plan === Plan.Personal ? personalBilledAnnually : teamBilledAnnually;

    return {
      buttonText: `Subscribe to ${billedAnnually ? 'annual' : 'monthly'} ${plan} plan`,
      paymentLink: `/payment?plan=${plan}&billed=${billedAnnually ? 'annually' : 'monthly'}`
    };
  };

  const getPrice = (
    billedAnnually: boolean,
    annualCurrency: string,
    annualPrice: number | string,
    monthlyCurrency: string,
    monthlyPrice: number | string
  ) =>
    billedAnnually
      ? `${annualCurrency}${annualPrice}`
      : `${monthlyCurrency}${monthlyPrice}`;

  const extractPrices = (
    product: StandardPaddleProduct | StandardPaddleProductWithAdditional
  ) => ({
    monthly: {
      currency: product.prices.monthly?.currency ?? 'ERROR',
      price: product.prices.monthly?.price ?? 'ERROR',
      additionalPrice:
        'monthlyAdditional' in product.prices
          ? product.prices.monthlyAdditional?.price
          : 'ERROR'
    },
    annual: {
      currency: product.prices.annual?.currency ?? 'ERROR',
      price: product.prices.annual?.price
        ? product.prices.annual.price / 12
        : 'ERROR',
      additionalPrice:
        'annualAdditional' in product.prices
          ? product.prices.annualAdditional.price / 12
          : 'ERROR'
    }
  });

  const personalPrices = extractPrices(products[Plan.Personal]);
  const teamPrices = extractPrices(products[Plan.Team]);
  const enterprisePrices = extractPrices(products[Plan.Enterprise]);

  return (
    <div className={styles.cardContainer}>
      <GitLiveLogo />
      <IonCard className={styles.card}>
        <IonCardHeader className={styles.cardHeader}>
          <IonCardTitle>Select a pricing plan</IonCardTitle>
          <IonCardSubtitle>
            Extend your existing plan or upgrade to a new plan
          </IonCardSubtitle>

          <a href="https://git.live/premium" target="_blank" rel="noreferrer">
            Learn more about our pricing plans →
          </a>
        </IonCardHeader>

        <IonRadioGroup value={selectedPlan}>
          <div className={styles.pricingContainer}>
            <div
              className={styles.basicContainer}
              onClick={() => setSelectedPlan(Plan.Free)}
            >
              <IonRadio
                color="primary"
                value={Plan.Free}
                onClick={() => setSelectedPlan(Plan.Free)}
              />
              <br />
              <div className={styles.pricingContent}>
                <h2 className={styles.pricingTitle}>Basic</h2>
                <div className={styles.pricingPrice}>Extend free trial</div>
                <div className={styles.pricingDescription}>
                  Invite more of your team to extend your free trial of all
                  features.
                </div>
                <div className={styles.pricingDescription}>
                  Get an additional week for every new team member once they
                  sign up.
                </div>
              </div>
            </div>

            <div
              className={styles.personalContainer}
              onClick={() => setSelectedPlan(Plan.Personal)}
            >
              <IonRadio
                color="light"
                value={Plan.Personal}
                onClick={() => setSelectedPlan(Plan.Personal)}
              />
              <br />
              <div className={styles.pricingContent}>
                <h2 className={styles.pricingTitle}>
                  Personal
                  <div className={styles.pricingBilled}>
                    <IonToggle
                      color="light"
                      checked={personalBilledAnnually}
                      onClick={() => setPersonalBilledAnnually((c) => !c)}
                    ></IonToggle>
                    <IonLabel>Billed Annually</IonLabel>
                  </div>
                </h2>
                <div className={styles.pricingPrice}>
                  {getPrice(
                    personalBilledAnnually,
                    personalPrices.annual.currency,
                    personalPrices.annual.price,
                    personalPrices.monthly.currency,
                    personalPrices.monthly.price
                  )}{' '}
                  <span>per month</span>
                </div>
                <div className={styles.pricingDescription}>
                  Unlock all features for just yourself with any team you work
                  with.
                </div>
                <div className={styles.pricingDescription}>Cancel anytime.</div>
              </div>
            </div>

            {enterprise ? (
              <div
                className={styles.teamContainer}
                onClick={() => setSelectedPlan(Plan.Enterprise)}
              >
                <IonRadio
                  color="light"
                  value={Plan.Enterprise}
                  onClick={() => setSelectedPlan(Plan.Enterprise)}
                />
                <br />
                <div className={styles.pricingContent}>
                  <h2 className={styles.pricingTitle}>
                    Enterprise
                    <div className={styles.pricingBilled}>
                      <IonToggle
                        color="light"
                        checked={teamBilledAnnually}
                        onClick={() => setTeamBilledAnnually((c) => !c)}
                      ></IonToggle>
                      <IonLabel>Billed Annually</IonLabel>
                    </div>
                  </h2>
                  <div className={styles.pricingPrice}>
                    {getPrice(
                      teamBilledAnnually,
                      enterprisePrices.annual.currency,
                      enterprisePrices.annual.price,
                      enterprisePrices.monthly.currency,
                      enterprisePrices.monthly.price
                    )}{' '}
                    <span>per month for up to 5 members</span>
                    <div className={styles.subPricing}>
                      <i>
                        {getPrice(
                          teamBilledAnnually,
                          enterprisePrices.annual.currency,
                          enterprisePrices.annual.additionalPrice,
                          enterprisePrices.monthly.currency,
                          enterprisePrices.monthly.additionalPrice
                        )}{' '}
                        <span>per additional member per month</span>
                      </i>
                    </div>
                  </div>
                  <div className={styles.pricingDescription}>
                    Unlock all features for your whole team.
                  </div>
                  <div className={styles.pricingDescription}>
                    Automated billing ensures you only get billed for active
                    members.
                  </div>
                  <div className={styles.pricingDescription}>
                    Cancel anytime.
                  </div>
                </div>
              </div>
            ) : (
              <div
                className={styles.teamContainer}
                onClick={() => setSelectedPlan(Plan.Team)}
              >
                <IonRadio
                  color="light"
                  value={Plan.Team}
                  onClick={() => setSelectedPlan(Plan.Team)}
                />
                <br />
                <div className={styles.pricingContent}>
                  <h2 className={styles.pricingTitle}>
                    Team
                    <div className={styles.pricingBilled}>
                      <IonToggle
                        color="light"
                        checked={teamBilledAnnually}
                        onClick={() => setTeamBilledAnnually((c) => !c)}
                      ></IonToggle>
                      <IonLabel>Billed Annually</IonLabel>
                    </div>
                  </h2>
                  <div className={styles.pricingPrice}>
                    {getPrice(
                      teamBilledAnnually,
                      teamPrices.annual.currency,
                      teamPrices.annual.price,
                      teamPrices.monthly.currency,
                      teamPrices.monthly.price
                    )}{' '}
                    <span>per month for up to 5 members</span>
                    <div className={styles.subPricing}>
                      <i>
                        {getPrice(
                          teamBilledAnnually,
                          teamPrices.annual.currency,
                          teamPrices.annual.additionalPrice,
                          teamPrices.monthly.currency,
                          teamPrices.monthly.additionalPrice
                        )}{' '}
                        <span>per additional member per month</span>
                      </i>
                    </div>
                  </div>
                  <div className={styles.pricingDescription}>
                    Unlock all features for your whole team.
                  </div>
                  <div className={styles.pricingDescription}>
                    Automated billing ensures you only get billed for active
                    members.
                  </div>
                  <div className={styles.pricingDescription}>
                    Cancel anytime.
                  </div>
                </div>
              </div>
            )}
          </div>
        </IonRadioGroup>

        <div className={styles.footer}>
          <IonButton
            color="primary"
            fill="solid"
            expand="block"
            href={paymentLink}
            disabled={!selectedPlan}
          >
            {buttonText}
          </IonButton>

          {selectedPlan !== Plan.Free && (
            <>
              <hr />
              <IonLabel className="ion-text-wrap text-margin">
                Not the bill payer?{' '}
                <a className={styles.btn} onClick={() => copyPaymentLink()}>
                  Copy shareable payment link{' '}
                  <span className={styles.arrow}>→</span>
                </a>
              </IonLabel>
            </>
          )}
        </div>
      </IonCard>

      <IonToast
        isOpen={linkCopied}
        message="Copied deep link to clipboard"
        onDidDismiss={() => setLinkCopied(false)}
        duration={3000}
        color="success"
        position="bottom"
        icon={checkmark}
        style={{ color: 'white' }}
      />
    </div>
  );
};
